
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    CubeIcon,
    ExclamationTriangleIcon,
    Cog6ToothIcon
} from '@heroicons/react/24/outline';
import './Dashboard.css';

// Define ZTool's localStorage keys
const AUTH_TOKEN_KEY = 'authToken';
const USER_DETAILS_KEY = 'loggedInUserDetails';

/**
 * Safely decodes a JWT and checks if its 'exp' (expiration) claim is in the past.
 */
const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) return true;
        const decodedJsonPayload = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
        const decodedPayload = JSON.parse(decodedJsonPayload);
        if (typeof decodedPayload.exp !== 'number') return true;
        const expirationTimeInMs = decodedPayload.exp * 1000;
        return Date.now() >= expirationTimeInMs;
    } catch (error) {
        console.error("Error decoding token:", error);
        return true;
    }
};

// --- Skeleton Loader Component ---
const DashboardSkeleton = () => (
    <main className="dashboard-main-area">
        <header className="page-header skeleton-header">
            <div className="skeleton-line title-skeleton"></div>
            <div className="skeleton-line subtitle-skeleton"></div>
        </header>
        <div className="applications-grid">
            {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="app-card skeleton-card">
                    <div className="skeleton-block icon-skeleton"></div>
                    <div className="app-card-content">
                        <div className="skeleton-line heading-skeleton"></div>
                        <div className="skeleton-line"></div>
                        <div className="skeleton-line short-skeleton"></div>
                    </div>
                    <div className="app-card-footer">
                        <div className="skeleton-line link-skeleton"></div>
                    </div>
                </div>
            ))}
        </div>
    </main>
);

const Dashboard = () => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const [userApps, setUserApps] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        setIsLoading(true);

        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (isTokenExpired(token)) {
            alert("Session is expired. Please log in again.");
            handleLogout(false);
            navigate('/login');
            return;
        }

        const storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
        let parsedUserDetails = null;

        try {
            parsedUserDetails = JSON.parse(storedUserDetailsString);
        } catch (e) {
            handleLogout();
            return;
        }

        if (!parsedUserDetails) {
            handleLogout();
            return;
        }

        // Check if the current user is active
        if (parsedUserDetails.active === false) {
            alert("Your account has been deactivated. Please contact an administrator.");
            handleLogout();
            return;
        }

        setCurrentUser(parsedUserDetails);
        window.loggedInUser = parsedUserDetails;

        if (parsedUserDetails.role?.name?.toUpperCase() === 'SUPER_ADMIN') {
            setIsSuperAdmin(true);
        }

        // --- UPDATED LOGIC FOR SORTING APPS BY ID ---
        const rawApps = parsedUserDetails.apps || [];
        
        // 1. Sort the raw apps by appId first
        const sortedApps = [...rawApps].sort((a, b) => {
            return (a.appId || 0) - (b.appId || 0);
        });

        // 2. Map the sorted apps to the UI structure
        const permittedApps = sortedApps.map(app => {
            const cleanAppName = app.appName.trim().toUpperCase();
            let appBaseUrl = null;

            if (cleanAppName === 'ZTASK' || cleanAppName === 'Z TASK') {
                appBaseUrl = process.env.REACT_APP_ZTASK_BASE_URL;
            } 
            else if (cleanAppName === 'ZORDER' || cleanAppName === 'Z ORDER') {
                appBaseUrl = process.env.REACT_APP_API_BASE_URL_FOR_ZORDER;
            }
            else if (cleanAppName === 'ZHR' || cleanAppName === 'Z HR') {
                appBaseUrl = process.env.REACT_APP_ZHR_BASE_URL;
            }
            else {
                const envKey = cleanAppName.replace(/\s+/g, '_');
                appBaseUrl = process.env[`REACT_APP_API_BASE_URL_FOR_${envKey}`];
            }

            return {
                id: app.appId, // Keeping ID for reference
                name: app.appName,
                apiKey: cleanAppName + "_" + app.appId, 
                description: app.appDescription || `Manage your ${app.appName.toLowerCase()} efficiently.`,
                icon: <CubeIcon className="w-8 h-8" />,
                isExternal: true,
                baseUrl: appBaseUrl,
                externalPath: '/'
            };
        });

        setUserApps(permittedApps);
        setIsLoading(false);

    }, [navigate]);

    const handleLogout = (shouldNavigate = true) => {
        window.loggedInUser = null;
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(USER_DETAILS_KEY);
        if (shouldNavigate) {
            navigate('/login');
        }
    };

    const handleAppAccess = (app) => {
        if (isTokenExpired(localStorage.getItem(AUTH_TOKEN_KEY))) {
            alert("Session is expired. Please log in again.");
            handleLogout();
            return;
        }

        if (!app.baseUrl) {
            alert(`The redirect URL for "${app.name}" is not configured. Please contact an administrator.`);
            return;
        }

        if (app.isExternal) {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            const userDetailsString = localStorage.getItem(USER_DETAILS_KEY);

            try {
                const userDetailsForExternal = JSON.parse(userDetailsString);
                const safeUserDetails = {
                    id: userDetailsForExternal.id, 
                    name: userDetailsForExternal.name, 
                    email: userDetailsForExternal.email,
                    active: userDetailsForExternal.active, 
                    apps: userDetailsForExternal.apps, 
                    role: userDetailsForExternal.role,
                };
                const encodedUserDetails = encodeURIComponent(JSON.stringify(safeUserDetails));
                
                const cleanBaseUrl = app.baseUrl.endsWith('/') ? app.baseUrl.slice(0, -1) : app.baseUrl;
                const targetUrl = `${cleanBaseUrl}${app.externalPath || ''}?token=${encodeURIComponent(token)}&userDetails=${encodedUserDetails}`;
                
                window.open(targetUrl, '_blank', 'noopener,noreferrer');
            } catch (e) {
                alert("There was an issue preparing your information.");
            }
        }
    };

    return (
        <div className="dashboard-page-container">
            <nav className="dashboard-navbar">
                <Link to="/dashboard" className="navbar-logo">ZTool</Link>
                <div className="navbar-user-section">
                    {isSuperAdmin && (
                        <Link to="/assign-apps" className="admin-action-link" title="Manage User Applications">
                            <Cog6ToothIcon className="w-5 h-5" />
                            <span>Assign Apps</span>
                        </Link>
                    )}
                    {currentUser && (
                         <span className="user-greeting">
                            <div className="user-avatar">{currentUser.name?.[0]?.toUpperCase() || 'U'}</div>
                            <div className="user-info-text">
                                Hello, <strong>{currentUser.name || 'User'}</strong>
                                {currentUser.role && <span className="user-role-display">({currentUser.role.name || 'Role N/A'})</span>}
                            </div>
                        </span>
                    )}
                    <button onClick={() => handleLogout()} className="logout-action-button">Logout</button>
                </div>
            </nav>

            {isLoading ? (
                <DashboardSkeleton />
            ) : (
                <main className="dashboard-main-area">
                    <header className="page-header">
                        <h1 className="page-title">Your ZTool Applications</h1>
                        <p className="page-subtitle">Access your tools to streamline tasks, tickets, and orders efficiently.</p>
                    </header>

                    {userApps.length > 0 ? (
                        <div className="applications-grid">
                            {userApps.map((app) => (
                                <div key={app.apiKey} className="app-card" onClick={() => handleAppAccess(app)} role="button" tabIndex={0} onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAppAccess(app); }}>
                                    <div className="app-card-icon-wrapper">{app.icon}</div>
                                    <div className="app-card-content">
                                        <h2 className="app-card-title">{app.name}</h2>
                                        <p className="app-card-description">{app.description}</p>
                                    </div>
                                    <div className="app-card-footer">
                                        <span className="app-access-text">Open {app.name} →</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-apps-container">
                            <ExclamationTriangleIcon className="no-apps-icon" />
                            <h2 className="no-apps-title">No Applications Assigned</h2>
                            <p className="no-apps-message">You currently do not have access to any applications. Please contact your administrator.</p>
                        </div>
                    )}
                </main>
            )}

            <footer className="dashboard-page-footer">
                © {new Date().getFullYear()} ZTool Inc. All rights reserved.
            </footer>
        </div>
    );
};

export default Dashboard;